import { useEffect, useMemo, useState } from 'react';

import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { motion } from 'framer-motion';
import { Navigate, Route, useSearchParams } from 'react-router-dom';

import CaseAssistant from './CaseAssistant';
import CaseSummary from './CaseSummary';
import StatusDisplay from './components/CaseProcessing';
import DocViewerFullFact from './components/DocViewer/components/DocViewerFullFact';
import NavigationTabs from './components/NavigationTabs';
import DataView from './DataView';
import Settings from './Settings';
import { RunStatus } from '../../../../../backend/src/types/types';
import { ForbiddenStatuses } from '../../../../../backend/src/types/types';
import Drawer from '../../../components/molecules/Drawer';
import InconsistencyViewer from '../../../components/molecules/InconsistencyViewer';
import Transcription from '../transcribe/Transcription';
import TranscriptList from '../transcribe/TranscriptList';
import { useGetCase } from '@/api/queries/useGetCase';
import { ApiError } from '@/api/types';
import { ErrorPage } from '@/components/molecules/ErrorPages/ErrorPage';
import { PageMissing } from '@/components/molecules/ErrorPages/PageMissing';
import { RequestMatterAccess } from '@/components/molecules/ErrorPages/RequestMatterAccess';
import { MainPageWrapper } from '@/components/molecules/PageWrappers/MainPageWrapper';
import { ReferenceDrawer } from '@/components/molecules/ReferenceDrawer';
import ToastDrawer from '@/components/molecules/ToastDrawer';
import { isProcessingStatus } from '@/constants/pipelineStatus';
import { useUserContext } from '@/Contexts/User';
import { useOpenInconsistencyViewer } from '@/hooks/useOpenInconsistencyViewer';
import { useStore } from '@/store';

const CaseEditor = () => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const { user } = useUserContext();

  const popupFact = useStore((state) => state.popupFact);
  const setPopupFact = useStore((state) => state.setPopupFact);

  // Inconsistency viewer
  const inconsistencyIds = searchParams.get('inconsistencyIds')?.split(',') || [];
  const [isInconsistencyViewerOpen, setIsInconsistencyViewerOpen] = useState(false);
  const { closeInconsistencyViewer } = useOpenInconsistencyViewer();

  // Case data
  const { isLoading: isLoadingCase, data: caseData, refetch: refetchCase, error: caseError } = useGetCase(caseId);

  // Use effect which polls the case for updates every 5 seconds if the case is processing
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const mostRecentRunStatus = caseData?.runs?.[0]?.status;
    if (!mostRecentRunStatus) return;

    if (isProcessingStatus(mostRecentRunStatus as RunStatus) && mostRecentRunStatus !== RunStatus.AwaitingApproval) {
      intervalId = setInterval(() => {
        refetchCase();
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [caseData, refetchCase]);

  useEffect(() => {
    if (inconsistencyIds && inconsistencyIds.length > 0) {
      setIsInconsistencyViewerOpen(true);
    } else {
      setIsInconsistencyViewerOpen(false);
    }
  }, [inconsistencyIds]);

  const renderContent = useMemo(() => {
    if (isLoadingCase) return null;

    // If there are any successful runs, show the summary page
    const successfulRuns = caseData?.runs?.filter((run) => run.status === RunStatus.Complete);
    if (successfulRuns && successfulRuns.length > 0) {
      return (
        <>
          {caseId && <NavigationTabs caseId={caseId} user={user} />}
          <div className="h-[calc(100%-2rem)] w-full">
            <Routes>
              <Route path="summary" element={<CaseSummary />} />
              <Route path="data/*" element={<DataView />} />
              <Route path="assistant" element={<CaseAssistant />} />
              <Route path="transcripts" element={<TranscriptList />} />
              <Route path="transcripts/:transcriptId" element={<Transcription />} />
              <Route path="settings" element={<Navigate to="settings/metadata" replace />} />
              <Route path="settings/*" element={<Settings />} />
              <Route path="*" element={<Navigate to={`/app/chronos/case-editor/summary`} />} />
            </Routes>
          </div>
        </>
      );
    }

    const mostRecentRunStatus = caseData?.runs?.[0]?.status || RunStatus.InQueue;
    const completedAtEstimate = caseData?.runs?.[0]?.completedAtEstimate;
    const pageEstimate = caseData?.runs?.[0]?.totalPages;

    if (
      caseData &&
      (isProcessingStatus(mostRecentRunStatus as RunStatus) || mostRecentRunStatus === RunStatus.Failed)
    ) {
      return (
        <StatusDisplay
          casePipelineStatus={mostRecentRunStatus as RunStatus}
          runId={caseData?.runs?.[0]?.id}
          completedAtEstimate={completedAtEstimate}
          pageEstimate={pageEstimate}
          durationEstimate={caseData?.runs?.[0]?.durationEstimate}
        />
      );
    }

    if (caseError && caseError instanceof ApiError) {
      if (caseError.message === ForbiddenStatuses.ForbiddenNoAccess) {
        return <RequestMatterAccess caseId={caseId} />;
      } else {
        return (
          <PageMissing
            title={'Sorry, we could not find this case'}
            description={
              <div>
                The page you are looking for does not exist, if you think this is an error please{' '}
                <a className="text-blue-500 hover:underline" href="mailto:support@wexler.ai">
                  contact support
                </a>
                .
              </div>
            }
          />
        );
      }
    }

    return (
      <ErrorPage
        title={'Failed to load'}
        description={
          <div>
            Sorry! We encountered a problem loading this page. Please <b>refresh the page</b> and try again. If the
            problem persists, please{' '}
            <a className="text-blue-500 hover:cursor-pointer" href="mailto:support@wexler.ai">
              contact
            </a>{' '}
            Wexler for support.
          </div>
        }
      />
    );
  }, [isLoadingCase, caseData, caseId]);

  return (
    <MainPageWrapper>
      <div className="h-full w-full">{renderContent}</div>
      <ReferenceDrawer />
      <Drawer
        children={
          inconsistencyIds &&
          caseId && (
            <InconsistencyViewer
              handleOpen={setIsInconsistencyViewerOpen}
              caseId={caseId}
              inconsistencyIds={inconsistencyIds}
            />
          )
        }
        hideCloseButton={true}
        isOpen={isInconsistencyViewerOpen}
        onClose={() => {
          closeInconsistencyViewer();
        }}
      />
      <ToastDrawer
        isOpen={popupFact !== null}
        toggleDrawer={() => setPopupFact(null)}
        position="left"
        width="725px"
        obscureBackground
      >
        {popupFact && (
          <>
            <motion.div key={popupFact.id}>
              <DocViewerFullFact fact={popupFact} showDocumentDetails={false} showComments />
            </motion.div>
          </>
        )}
      </ToastDrawer>
    </MainPageWrapper>
  );
};

export default CaseEditor;
